import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  Box, Typography, TextField, InputAdornment, List, ListItem,
  ListItemButton, ListItemAvatar, Avatar, ListItemText, Divider,
  useTheme, alpha, ToggleButtonGroup, ToggleButton, Tooltip,
  IconButton, Menu, MenuItem,
} from '@mui/material';
import {
  Search as SearchIcon, Person as PersonIcon,
  SortByAlpha as SortIcon,
} from '@mui/icons-material';
import { Membre, Relation } from '../types';

const SIDEBAR_WIDTH = 268;

interface Props {
  open: boolean;
  membres: Membre[];
  relations: Relation[];
  selectedId: number | null;
  onSelect: (id: number) => void;
}

type FilterMode = 'tous' | 'vivants' | 'decedes';
type SortMode = 'nom' | 'naissance' | 'deces';

const genderColor = (sexe?: string) => {
  if (sexe === 'Homme') return '#3B82F6';
  if (sexe === 'Femme') return '#EC4899';
  return '#6B7280';
};

const getInitials = (prenom: string, nom: string) =>
  `${prenom?.[0] || ''}${nom?.[0] || ''}`.toUpperCase();

const formatYears = (naissance?: string, deces?: string) => {
  if (!naissance && !deces) return null;
  const birth = naissance ? new Date(naissance).getFullYear() : '?';
  if (deces) return `${birth} – ${new Date(deces).getFullYear()}`;
  return `${birth}`;
};

const MemberSidebar: React.FC<Props> = ({ open, membres, relations, selectedId, onSelect }) => {
  const theme = useTheme();
  const [search, setSearch] = useState('');
  const [filterMode, setFilterMode] = useState<FilterMode>('tous');
  const [sortMode, setSortMode] = useState<SortMode>('nom');
  const [sortAnchor, setSortAnchor] = useState<null | HTMLElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Ctrl+F focuses the search
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'f' && open) {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open]);

  const filtered = useMemo(() => {
    let list = membres.filter(m => {
      const matchSearch = `${m.prenom} ${m.nom} ${m.nom_jeune_fille || ''}`
        .toLowerCase().includes(search.toLowerCase());
      if (!matchSearch) return false;
      if (filterMode === 'vivants') return !m.date_deces;
      if (filterMode === 'decedes') return !!m.date_deces;
      return true;
    });

    if (sortMode === 'nom') {
      list = list.sort((a, b) => `${a.nom}${a.prenom}`.localeCompare(`${b.nom}${b.prenom}`));
    } else if (sortMode === 'naissance') {
      list = list.sort((a, b) => {
        if (!a.date_naissance && !b.date_naissance) return 0;
        if (!a.date_naissance) return 1;
        if (!b.date_naissance) return -1;
        return a.date_naissance.localeCompare(b.date_naissance);
      });
    } else if (sortMode === 'deces') {
      list = list.sort((a, b) => {
        if (!a.date_deces && !b.date_deces) return 0;
        if (!a.date_deces) return 1;
        if (!b.date_deces) return -1;
        return a.date_deces.localeCompare(b.date_deces);
      });
    }
    return list;
  }, [membres, search, filterMode, sortMode]);

  const vivants = useMemo(() => membres.filter(m => !m.date_deces).length, [membres]);
  const decedes = membres.length - vivants;

  const SORT_LABELS: Record<SortMode, string> = {
    nom: 'Par nom',
    naissance: 'Par naissance',
    deces: 'Par décès',
  };

  return (
    <Box
      sx={{
        width: open ? SIDEBAR_WIDTH : 0,
        flexShrink: 0,
        overflow: 'hidden',
        transition: 'width 0.25s ease',
        borderRight: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: theme.palette.background.paper,
      }}
    >
      <Box sx={{ width: SIDEBAR_WIDTH, display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <Box sx={{ p: 1.5, pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography
              variant="caption" fontWeight={700} color="text.disabled"
              sx={{ textTransform: 'uppercase', letterSpacing: '0.08em' }}
            >
              Membres ({membres.length})
            </Typography>
            <Tooltip title={SORT_LABELS[sortMode]} placement="bottom">
              <IconButton size="small" onClick={e => setSortAnchor(e.currentTarget)} sx={{ p: 0.4 }}>
                <SortIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              </IconButton>
            </Tooltip>
          </Box>
          <TextField
            size="small"
            placeholder="Rechercher… (Ctrl+F)"
            value={search}
            onChange={e => setSearch(e.target.value)}
            fullWidth
            inputRef={searchRef}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2, fontSize: '0.85rem',
                bgcolor: alpha(theme.palette.text.primary, 0.04),
                '& fieldset': { border: 'none' },
                '&:hover fieldset': { border: 'none' },
                '&.Mui-focused fieldset': { border: `1px solid ${theme.palette.primary.main}` },
              },
            }}
          />
        </Box>

        {/* Filter tabs */}
        <Box sx={{ px: 1.5, pb: 1 }}>
          <ToggleButtonGroup
            value={filterMode}
            exclusive
            onChange={(_, v) => { if (v) setFilterMode(v); }}
            size="small"
            fullWidth
            sx={{
              '& .MuiToggleButton-root': {
                py: 0.3, fontSize: '0.72rem', fontWeight: 600,
                textTransform: 'none', border: 'none',
                borderRadius: '6px !important',
                color: 'text.secondary',
                '&.Mui-selected': {
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: 'primary.main',
                },
              },
            }}
          >
            <ToggleButton value="tous">Tous ({membres.length})</ToggleButton>
            <ToggleButton value="vivants">Vivants ({vivants})</ToggleButton>
            <ToggleButton value="decedes">Décédés ({decedes})</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Divider />

        {/* Sort menu */}
        <Menu anchorEl={sortAnchor} open={!!sortAnchor} onClose={() => setSortAnchor(null)}>
          {(['nom', 'naissance', 'deces'] as SortMode[]).map(s => (
            <MenuItem
              key={s} selected={sortMode === s}
              onClick={() => { setSortMode(s); setSortAnchor(null); }}
              sx={{ fontSize: '0.85rem' }}
            >
              {SORT_LABELS[s]}
            </MenuItem>
          ))}
        </Menu>

        {/* List */}
        <List disablePadding sx={{ flex: 1, overflowY: 'auto' }}>
          {filtered.length === 0 && (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <PersonIcon sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body2" color="text.disabled" fontSize="0.8rem">
                {search ? 'Aucun résultat' : 'Aucun membre'}
              </Typography>
            </Box>
          )}
          {filtered.map((m, i) => {
            const color = genderColor(m.sexe);
            const years = formatYears(m.date_naissance, m.date_deces);
            const isDeceased = !!m.date_deces;
            return (
              <React.Fragment key={m.id}>
                <ListItem disablePadding>
                  <ListItemButton
                    selected={selectedId === m.id}
                    onClick={() => onSelect(m.id)}
                    sx={{
                      py: 1, px: 1.5,
                      opacity: isDeceased ? 0.75 : 1,
                      '&.Mui-selected': {
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        borderRight: `3px solid ${theme.palette.primary.main}`,
                      },
                      '&.Mui-selected:hover': { bgcolor: alpha(theme.palette.primary.main, 0.12) },
                    }}
                  >
                    <ListItemAvatar sx={{ minWidth: 40 }}>
                      <Avatar
                        sx={{
                          width: 32, height: 32,
                          bgcolor: alpha(color, 0.15),
                          color, fontWeight: 700, fontSize: '0.75rem',
                        }}
                      >
                        {getInitials(m.prenom, m.nom)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight={600} noWrap fontSize="0.85rem">
                          {m.prenom} {m.nom}
                          {isDeceased && <span style={{ marginLeft: 4, fontSize: '0.8em', color: '#9CA3AF' }}>†</span>}
                        </Typography>
                      }
                      secondary={
                        years || m.sexe ? (
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {[years, m.sexe].filter(Boolean).join(' · ')}
                          </Typography>
                        ) : null
                      }
                    />
                  </ListItemButton>
                </ListItem>
                {i < filtered.length - 1 && (
                  <Divider variant="inset" component="li" sx={{ ml: '52px' }} />
                )}
              </React.Fragment>
            );
          })}
        </List>

        {/* Footer stats */}
        <Divider />
        <Box sx={{ px: 1.5, py: 1, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {[
            { label: 'personnes', count: membres.length },
            { label: 'liens', count: relations.length },
          ].map(stat => (
            <Box key={stat.label}>
              <Typography variant="caption" color="text.disabled" fontSize="0.7rem">
                {stat.count} {stat.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default MemberSidebar;
